"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  SitePlanEditor,
  SitePlanPrint,
  useSitePlan,
  useSitePlanBackground,
  type PlanElement,
} from "@/components/tools/SitePlanCanvas";
import {
  CheckboxRow,
  CompletionPanel,
  CopyButton,
  EvidenceFields,
  EvidencePrint,
  Field,
  GuardedPrintButton,
  PrintFooter,
  TextArea,
  TextInput,
  evidenceRequiredItems,
  trackToolResult,
  useSavedEvidence,
} from "@/components/tools/ui";

type ScopeId = "traffic" | "fire" | "welfare" | "lifting" | "public" | "services" | "hotworks";

interface Scope {
  id: ScopeId;
  label: string;
  printLabel: string;
  checks: { id: string; label: string; done: (f: PlanFacts, notes: string) => boolean }[];
}

interface PlanFacts {
  routes: Record<string, number>;
  zones: Record<string, number>;
  barriers: Record<string, number>;
  icons: Record<string, number>;
  labels: number;
  total: number;
}

const SCOPES: Scope[] = [
  {
    id: "traffic",
    label: "Plant, vehicles or deliveries",
    printLabel: "Plant / vehicle movement",
    checks: [
      { id: "traffic-vehicle", label: "Vehicle or delivery route marked", done: (f) => f.routes.vehicle + f.routes.delivery > 0 },
      { id: "traffic-pedestrian", label: "Pedestrian route or safe walkway marked", done: (f) => f.routes.pedestrian + f.barriers.walkway > 0 },
      { id: "traffic-exclusion", label: "Exclusion zone or segregated area shown", done: (f) => f.zones.exclusion + f.barriers.barrier > 0 },
    ],
  },
  {
    id: "fire",
    label: "Fire plan or emergency arrangements",
    printLabel: "Fire / emergency arrangements",
    checks: [
      { id: "fire-escape", label: "Escape route shown", done: (f) => f.routes.escape > 0 },
      { id: "fire-point", label: "Fire point or extinguisher location shown", done: (f) => f.icons.fire > 0 },
      { id: "fire-assembly", label: "Assembly point shown", done: (f) => f.icons.assembly > 0 },
    ],
  },
  {
    id: "welfare",
    label: "Welfare, first aid or induction plan",
    printLabel: "Welfare / first aid",
    checks: [
      { id: "welfare-unit", label: "Welfare area or unit marked", done: (f) => f.icons.welfare + f.zones.welfare > 0 },
      { id: "welfare-firstaid", label: "First aid point marked", done: (f) => f.icons.firstaid > 0 },
      { id: "welfare-access", label: "Access route to welfare or first aid marked", done: (f, notes) => f.routes.pedestrian > 0 || /access|walkway|route/i.test(notes) },
    ],
  },
  {
    id: "lifting",
    label: "Lifting operation or crane set-up",
    printLabel: "Lifting operation",
    checks: [
      { id: "lifting-crane", label: "Crane or lifting position marked", done: (f) => f.icons.crane > 0 },
      { id: "lifting-radius", label: "Lifting exclusion zone shown", done: (f) => f.zones.exclusion > 0 },
      { id: "lifting-laydown", label: "Load laydown / storage area shown", done: (f) => f.icons.storage + f.zones.laydown > 0 },
    ],
  },
  {
    id: "public",
    label: "Public interface, occupied site or neighbours",
    printLabel: "Public interface",
    checks: [
      { id: "public-zone", label: "Public interface or boundary marked", done: (f) => f.zones.public + f.barriers.boundary > 0 },
      {
        id: "public-route",
        label: "Pedestrian diversion or protected route considered",
        done: (f, notes) => f.routes.pedestrian + f.barriers.walkway + f.barriers.barrier > 0 || /public|footpath|neighbour|occupied/i.test(notes),
      },
    ],
  },
  {
    id: "services",
    label: "Live services, overhead lines or isolations",
    printLabel: "Services / isolations",
    checks: [
      {
        id: "services-live",
        label: "Live service, isolation or overhead restriction marked",
        done: (f) =>
          f.icons.electric +
            f.icons.overhead +
            f.icons.water +
            f.barriers["service-gas"] +
            f.barriers["service-water"] +
            f.barriers["service-electric"] >
          0,
      },
      { id: "services-permit", label: "Permit or controlled work point shown", done: (f) => f.icons.permit + (f.icons.dig ?? 0) + (f.icons.isolation ?? 0) > 0 },
    ],
  },
  {
    id: "hotworks",
    label: "Hot works or process fire risk",
    printLabel: "Hot works",
    checks: [
      { id: "hotworks-area", label: "Hot works area marked", done: (f) => f.zones.hotworks > 0 },
      { id: "hotworks-fire", label: "Fire point / extinguisher shown near the work area", done: (f) => f.icons.fire > 0 },
      { id: "hotworks-permit", label: "Permit-controlled work point shown", done: (f) => f.icons.permit + (f.icons.permitboard ?? 0) + (f.icons.holdpoint ?? 0) > 0 },
    ],
  },
];

const BASE_CHECKS = [
  { id: "base-site", label: "Site / project named" },
  { id: "base-purpose", label: "Plan purpose selected" },
  { id: "base-map", label: "Background drawing or sketch elements added" },
  { id: "base-orientation", label: "North arrow or orientation note added" },
  { id: "base-gate", label: "Access gate or entry point shown" },
  { id: "base-notes", label: "Briefing notes / assumptions recorded" },
];

function count<T extends string>(arr: T[], key: T) {
  return arr.filter((v) => v === key).length;
}

function countBy(values: string[]): Record<string, number> {
  const out: Record<string, number> = {};
  values.forEach((v) => {
    out[v] = (out[v] ?? 0) + 1;
  });
  return out;
}

function planFacts(elements: PlanElement[]): PlanFacts {
  const routeTypes = elements.filter((e) => e.kind === "route").map((e) => e.routeType);
  const zoneTypes = elements.filter((e) => e.kind === "zone").map((e) => e.zoneType);
  const barrierTypes = elements.filter((e) => e.kind === "barrier").map((e) => e.barrierType);
  const iconTypes = elements.filter((e) => e.kind === "icon").map((e) => e.icon);
  const routeKeys = ["vehicle", "pedestrian", "escape", "delivery", "emergency"] as const;
  // Scope checks reference these keys directly, so they must always exist —
  // every other type (the palette keeps growing) is counted dynamically on top.
  const zoneKeys = ["exclusion", "building", "hotworks", "laydown", "public", "welfare"] as const;
  const barrierKeys = ["boundary", "barrier", "walkway", "service-gas", "service-water", "service-electric"] as const;
  const iconKeys = [
    "gate",
    "fire",
    "assembly",
    "firstaid",
    "welfare",
    "storage",
    "crane",
    "north",
    "electric",
    "overhead",
    "water",
    "permit",
    "spill",
  ] as const;
  return {
    routes: Object.fromEntries(routeKeys.map((k) => [k, count(routeTypes, k)])),
    zones: { ...Object.fromEntries(zoneKeys.map((k) => [k, 0])), ...countBy(zoneTypes) },
    barriers: { ...Object.fromEntries(barrierKeys.map((k) => [k, 0])), ...countBy(barrierTypes) },
    icons: { ...Object.fromEntries(iconKeys.map((k) => [k, 0])), ...countBy(iconTypes) },
    labels: elements.filter((e) => e.kind === "label").length,
    total: elements.length,
  };
}

function elementLabel(el: PlanElement): string {
  switch (el.kind) {
    case "route":
      return `${el.routeType.replace("-", " ")} route`;
    case "barrier":
      return `${el.barrierType.replace("-", " ")} line`;
    case "zone":
      return el.label.trim() || `${el.zoneType.replace("-", " ")} zone`;
    case "icon":
      return `${el.icon.replace("-", " ")} marker`;
    case "label":
      return el.text.trim() || "Text label";
    case "draw":
      return el.highlight ? "Highlighted area" : "Freehand mark";
    case "line":
      return el.arrow ? "Arrow" : "Line";
    case "shape":
      return `${el.shape} shape`;
    case "image":
      return `Image — ${el.name}`;
    case "measure":
      return "Measurement";
    case "calibration":
      return `Scale reference (${el.metres} m)`;
    case "legend":
      return "Plan key / legend";
  }
}

function buildBriefingText({
  site,
  purpose,
  selectedScopes,
  elements,
  notes,
}: {
  site: string;
  purpose: string;
  selectedScopes: Scope[];
  elements: PlanElement[];
  notes: string;
}) {
  const lines = [
    `Site safety plan sketch${site.trim() ? ` - ${site.trim()}` : ""}`,
    purpose.trim() ? `Purpose: ${purpose.trim()}` : "Purpose: site briefing / RAMS support",
    "",
    "Briefing points:",
    "- Confirm the sketch against the actual site before briefing or issuing the RAMS.",
    "- Walk the marked routes and zones with the supervisor before work starts.",
    "- Stop and update the sketch if access, other trades, services, weather or the work sequence changes.",
  ];
  selectedScopes.forEach((scope) => lines.push(`- Check the marked controls for: ${scope.printLabel}.`));
  if (notes.trim()) lines.push("", "Notes:", notes.trim());
  if (elements.length > 0) {
    lines.push("", "Plan register:");
    elements.slice(0, 30).forEach((el, i) => lines.push(`${i + 1}. ${elementLabel(el)}`));
  }
  return lines.join("\n");
}

function downloadJson(filename: string, payload: unknown) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function slugifySite(site: string) {
  const slug = site
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || "site-safety-plan";
}

export function SiteSafetyPlanSketcher() {
  const [site, setSite] = useState("");
  const [purpose, setPurpose] = useState("");
  const [preparedFor, setPreparedFor] = useState("");
  const [notes, setNotes] = useState("");
  const [scopes, setScopes] = useState<Record<ScopeId, boolean>>({
    traffic: true,
    fire: true,
    welfare: true,
    lifting: false,
    public: false,
    services: false,
    hotworks: false,
  });
  const [sitePlan, setSitePlan] = useSitePlan("site-safety-plan-sketcher");
  const [background, setBackground] = useSitePlanBackground("site-safety-plan-sketcher");
  const [evidence, setEvidence, clearEvidence] = useSavedEvidence();
  const tracked = useRef(false);

  const selectedScopes = useMemo(() => SCOPES.filter((scope) => scopes[scope.id]), [scopes]);
  const facts = useMemo(() => planFacts(sitePlan), [sitePlan]);

  useEffect(() => {
    if (!tracked.current && sitePlan.length >= 3) {
      tracked.current = true;
      trackToolResult("site-safety-plan-sketcher", { elements: sitePlan.length, scopes: selectedScopes.length });
    }
  }, [selectedScopes.length, sitePlan.length]);

  const baseItems = [
    { label: BASE_CHECKS[0].label, done: site.trim().length > 0 },
    { label: BASE_CHECKS[1].label, done: selectedScopes.length > 0 },
    { label: BASE_CHECKS[2].label, done: sitePlan.length > 0 || !!background?.dataUrl },
    { label: BASE_CHECKS[3].label, done: facts.icons.north > 0 || /north|entrance|front|rear|left|right|east|west|south/i.test(notes) },
    { label: BASE_CHECKS[4].label, done: facts.icons.gate > 0 || /gate|entrance|access/i.test(notes) },
    { label: BASE_CHECKS[5].label, done: notes.trim().length > 0 },
  ];
  const scopeItems = selectedScopes.flatMap((scope) =>
    scope.checks.map((check) => ({
      label: `${scope.printLabel}: ${check.label}`,
      done: check.done(facts, notes),
    })),
  );
  const requiredItems = [...baseItems, ...scopeItems, ...evidenceRequiredItems(evidence)];
  const missingRequired = requiredItems.filter((i) => !i.done).map((i) => i.label);
  const doneCount = requiredItems.filter((i) => i.done).length;
  const completion = requiredItems.length > 0 ? Math.round((doneCount / requiredItems.length) * 100) : 0;

  const planRegister = sitePlan.map((el, i) => ({ id: el.id, index: i + 1, label: elementLabel(el), kind: el.kind }));

  const briefingText = buildBriefingText({ site, purpose, selectedScopes, elements: sitePlan, notes });
  const hasContent = site.trim() || purpose.trim() || sitePlan.length > 0 || background?.dataUrl;

  return (
    <div className="grid min-w-0 gap-6 lg:grid-cols-[minmax(0,1fr)_420px] 2xl:grid-cols-[minmax(0,1fr)_460px]">
      <div className="min-w-0 space-y-6">
        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Plan details</h2>
          <p className="mt-1 text-sm text-muted">
            Build a marked-up site sketch that can sit behind a RAMS, CPP, fire plan, lift plan or site briefing.
          </p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <Field label="Site / project">
              <TextInput value={site} onChange={(e) => setSite(e.target.value)} placeholder="e.g. Plot 4, Mill Lane" />
            </Field>
            <Field label="Prepared for">
              <TextInput value={preparedFor} onChange={(e) => setPreparedFor(e.target.value)} placeholder="Client / principal contractor / supervisor" />
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Plan purpose" hint="What this sketch will be used for.">
              <TextInput
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="e.g. Site setup sketch for RAMS briefing and construction phase plan"
              />
            </Field>
          </div>
        </div>

        <div className="card shadow-soft p-6">
          <h2 className="text-xl">What the sketch must cover</h2>
          <p className="mt-1 text-sm text-muted">
            Pick the planning layers that apply. The checker will look for the matching routes, zones and markers.
          </p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {SCOPES.map((scope) => (
              <CheckboxRow
                key={scope.id}
                checked={!!scopes[scope.id]}
                onChange={(v) => setScopes((s) => ({ ...s, [scope.id]: v }))}
              >
                {scope.label}
              </CheckboxRow>
            ))}
          </div>
        </div>

        <SitePlanEditor
          value={sitePlan}
          onChange={setSitePlan}
          background={background}
          onBackgroundChange={setBackground}
          title="Site safety plan sketch"
          hint="Upload a plan or photo, then mark up the routes, exclusion zones, emergency points, services, welfare and permit-controlled areas. The output prints as a RAMS-ready plan."
        />

        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Briefing notes and assumptions</h2>
          <p className="mt-1 text-sm text-muted">
            Record what the drawing cannot show: orientation, temporary changes, other trades, access restrictions and review triggers.
          </p>
          <div className="mt-4">
            <Field label="Notes for the supervisor / RAMS reviewer">
              <TextArea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. North is the site entrance side. Pedestrian route is barriered. Update the sketch if scaffold line, crane radius or delivery gate changes."
              />
            </Field>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <CopyButton tool="site-safety-plan-sketcher" buildText={() => briefingText} label="Copy briefing notes" />
            <button
              type="button"
              onClick={() =>
                downloadJson(`${slugifySite(site)}-sketch.json`, {
                  site,
                  purpose,
                  preparedFor,
                  scopes,
                  notes,
                  evidence,
                  elements: sitePlan,
                  background: background ? { name: background.name, opacity: background.opacity, embedded: true } : null,
                })
              }
              className="inline-flex items-center justify-center rounded-full border border-line-2 px-6 py-3 text-[0.95rem] font-semibold text-ink transition-all hover:border-ink hover:bg-mist"
            >
              Export plan data
            </button>
          </div>
        </div>

        <EvidenceFields value={evidence} onChange={setEvidence} onClear={clearEvidence} />
      </div>

      <div className="min-w-0 space-y-4">
        <div className="min-w-0 space-y-4 lg:sticky lg:top-20 lg:z-10">
          <CompletionPanel items={requiredItems} title="Plan intelligence" />
          <div className="print-hide card shadow-soft p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[0.78rem] font-bold uppercase tracking-wide text-muted">Detected layers</span>
              <span className="font-display text-sm font-bold text-ink">{completion}%</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-[0.82rem]">
              <div className="rounded-lg bg-mist px-3 py-2"><span className="font-semibold text-ink">{facts.total}</span> elements</div>
              <div className="rounded-lg bg-mist px-3 py-2"><span className="font-semibold text-ink">{Object.values(facts.routes).reduce((a, b) => a + b, 0)}</span> routes</div>
              <div className="rounded-lg bg-mist px-3 py-2"><span className="font-semibold text-ink">{Object.values(facts.zones).reduce((a, b) => a + b, 0)}</span> zones</div>
              <div className="rounded-lg bg-mist px-3 py-2"><span className="font-semibold text-ink">{Object.values(facts.icons).reduce((a, b) => a + b, 0)}</span> markers</div>
            </div>
            {missingRequired.length > 0 ? (
              <div className="mt-3 rounded-lg border border-risk-high/40 bg-[#fef0e6] px-3 py-2 text-[0.82rem] text-[#8a3d0c]">
                Next gap: <span className="font-semibold">{missingRequired[0]}</span>
              </div>
            ) : (
              <div className="mt-3 rounded-lg border border-emerald/40 bg-emerald-tint px-3 py-2 text-[0.82rem] font-semibold text-emerald-deep">
                The sketch has the core layers selected for this plan.
              </div>
            )}
          </div>
        </div>

        {hasContent ? (
          <>
            <div className="print-area card shadow-soft p-6" id="site-safety-plan-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">Site safety plan sketch</div>
              <h2 className="mt-1 text-lg">
                {site.trim() || "Site / project"}
                {purpose.trim() ? ` - ${purpose.trim()}` : ""}
              </h2>
              {preparedFor.trim() && <p className="mt-1 text-sm text-muted">Prepared for: {preparedFor.trim()}</p>}

              <div className="mt-4">
                <h3 className="text-[0.92rem] font-bold text-ink">Plan covers</h3>
                <ul className="mt-1.5 flex flex-wrap gap-1.5">
                  {selectedScopes.length > 0 ? (
                    selectedScopes.map((scope) => (
                      <li key={scope.id} className="rounded-full bg-mist px-2.5 py-1 text-[0.8rem] text-ink">
                        {scope.printLabel}
                      </li>
                    ))
                  ) : (
                    <li className="text-sm text-muted">No layers selected.</li>
                  )}
                </ul>
              </div>

              <SitePlanPrint value={sitePlan} background={background} heading="Marked-up site sketch" />

              {notes.trim() && (
                <div className="mt-5 avoid-break">
                  <h3 className="font-display text-[0.95rem] font-bold uppercase tracking-wide text-ink">Briefing notes</h3>
                  <p className="mt-2 whitespace-pre-wrap text-[0.86rem] leading-snug text-body">{notes.trim()}</p>
                </div>
              )}

              {planRegister.length > 0 && (
                <div className="mt-5 avoid-break">
                  <h3 className="font-display text-[0.95rem] font-bold uppercase tracking-wide text-ink">Plan register</h3>
                  <table className="mt-2 w-full text-[0.84rem]">
                    <thead>
                      <tr className="border-b border-line text-left text-[0.72rem] uppercase tracking-wide text-muted">
                        <th className="w-8 py-1.5 pr-2 font-semibold">#</th>
                        <th className="py-1.5 pr-2 font-semibold">Item</th>
                        <th className="w-24 py-1.5 font-semibold">Layer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {planRegister.map((item) => (
                        <tr key={item.id} className="border-b border-line/60 align-top">
                          <td className="py-1.5 pr-2 text-muted">{item.index}</td>
                          <td className="py-1.5 pr-2 text-ink">{item.label}</td>
                          <td className="py-1.5 text-body">{item.kind}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-5 rounded-lg border border-line bg-mist/55 p-4 text-[0.84rem] leading-relaxed text-body">
                Diagram only - not to scale. Confirm the marked routes, zones, services and emergency points on site
                before briefing workers or attaching this to a RAMS, CPP, fire plan, traffic plan or permit pack.
                Revise the sketch if site conditions, work sequence, access, adjacent trades or emergency arrangements change.
              </div>

              <EvidencePrint value={evidence} briefing />
              <PrintFooter toolName="site safety plan sketcher" />
            </div>
            <GuardedPrintButton
              tool="site-safety-plan-sketcher"
              label="Print / save sketch as PDF"
              missing={missingRequired}
              warningTitle="This site safety sketch still has gaps"
            />
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Name the site, upload a plan or add a few sketch elements to create the printable record.
          </div>
        )}
      </div>
    </div>
  );
}
